import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { adminApi } from '../../services/adminService'
import type { 
  AdminFormularyItem,
  AdminFormularyListResponse,
  PharmacyListItem,
  AdminPharmacyListResponse 
} from '../../services/adminService'
import type { FormularyQueryParams } from '../../services/formularyService'

// Async thunks
export const fetchAdminFormularies = createAsyncThunk(
  'admin/fetchFormularies',
  async (params?: FormularyQueryParams) => {
    const response = await adminApi.getFormularies(params)
    return response
  }
)

export const fetchAdminPharmacies = createAsyncThunk(
  'admin/fetchPharmacies',
  async (params?: { page_size?: number; page_number?: number; name?: string[] }) => {
    const response = await adminApi.getPharmacies(params)
    return response
  }
)

// Interface for pharmacy editing data
interface PharmacyEditData {
  selected: boolean;
  price: number;
}

// State interface
interface AdminState {
  formularies: AdminFormularyItem[]
  pharmacies: PharmacyListItem[]
  loading: boolean
  error: string | null
  
  // Ephemeral editing state for pharmacy management in formulary dialog
  editingFormularyId: string | null
  pharmacyEditData: Record<string, PharmacyEditData> // pharmacyId -> edit data
}

// Initial state
const initialState: AdminState = {
  formularies: [],
  pharmacies: [],
  loading: false,
  error: null,
  editingFormularyId: null,
  pharmacyEditData: {},
}

// Slice
const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    // Clear error
    clearError: (state) => {
      state.error = null
    },
    
    // Initialize pharmacy editing for a formulary
    initializePharmacyEditing: (state, action) => {
      const { formularyId, currentPharmacies, availablePharmacies } = action.payload
      state.editingFormularyId = formularyId
      
      // Initialize edit data for all available pharmacies
      const newPharmacyEditData: Record<string, PharmacyEditData> = {}
      availablePharmacies.forEach(pharmacy => {
        const existingPharmacy = currentPharmacies.find(p => p.id === pharmacy.id)
        newPharmacyEditData[pharmacy.id] = {
          selected: !!existingPharmacy,
          price: existingPharmacy?.price || 100.00
        }
      })
      
      state.pharmacyEditData = newPharmacyEditData
    },
    
    // Update pharmacy selection
    updatePharmacySelection: (state, action) => {
      const { pharmacyId, selected } = action.payload
      if (state.pharmacyEditData[pharmacyId]) {
        state.pharmacyEditData[pharmacyId].selected = selected
      } else {
        state.pharmacyEditData[pharmacyId] = { selected, price: 100.00 }
      }
    },
    
    // Update pharmacy price
    updatePharmacyPrice: (state, action) => {
      const { pharmacyId, price } = action.payload
      if (state.pharmacyEditData[pharmacyId]) {
        state.pharmacyEditData[pharmacyId].price = price
        state.pharmacyEditData[pharmacyId].selected = true // Auto-select when price is set
      } else {
        state.pharmacyEditData[pharmacyId] = { selected: true, price }
      }
    },
    
    // Clear pharmacy editing state
    clearPharmacyEditing: (state) => {
      state.editingFormularyId = null
      state.pharmacyEditData = {}
    },
  },
  extraReducers: (builder) => {
    // Fetch admin formularies
    builder
      .addCase(fetchAdminFormularies.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAdminFormularies.fulfilled, (state, action) => {
        state.loading = false
        state.formularies = action.payload.items
      })
      .addCase(fetchAdminFormularies.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch formularies'
      })

    // Fetch admin pharmacies
    builder
      .addCase(fetchAdminPharmacies.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAdminPharmacies.fulfilled, (state, action) => {
        state.loading = false
        state.pharmacies = action.payload.items
      })
      .addCase(fetchAdminPharmacies.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch pharmacies'
      })
  },
})

// Export actions
export const { 
  clearError,
  initializePharmacyEditing,
  updatePharmacySelection,
  updatePharmacyPrice,
  clearPharmacyEditing
} = adminSlice.actions

// Selectors
export const selectAdminFormularies = (state: { admin: AdminState }) => state.admin.formularies
export const selectAdminPharmacies = (state: { admin: AdminState }) => state.admin.pharmacies
export const selectAdminLoading = (state: { admin: AdminState }) => state.admin.loading
export const selectAdminError = (state: { admin: AdminState }) => state.admin.error
export const selectEditingFormularyId = (state: { admin: AdminState }) => state.admin.editingFormularyId
export const selectPharmacyEditData = (state: { admin: AdminState }) => state.admin.pharmacyEditData

export default adminSlice.reducer